import Grid from "@mui/material/Unstable_Grid2";
import { v4 as uuid } from "uuid";
import { AnswerOption, InputType, Question } from "../../API";
import {
  Control,
  FormState,
  UseFieldArrayRemove,
  UseFormGetValues,
  UseFormRegister,
  UseFormWatch,
  useFieldArray
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { EditAnswerOption } from "../AnswerOptions/EditAnswerOption";
import { useState } from "react";
import { QuestionErrors } from "../Instruments/instrumentUtils";
import { Alert } from "@mui/material";
import { InstrumentWithoutAuthor } from "../Instruments/EditInstrument";

interface EditQuestionProps {
  question: Question;
  anyApiIsLoading: boolean;
  isAssessmentPublished: boolean;
  register: UseFormRegister<InstrumentWithoutAuthor>;
  index: number;
  removeQuestion: UseFieldArrayRemove;
  control: Control<InstrumentWithoutAuthor, any>;
  questionIndex: number;
  getValues: UseFormGetValues<InstrumentWithoutAuthor>;
  formState: FormState<InstrumentWithoutAuthor>;
  watch: UseFormWatch<InstrumentWithoutAuthor>;
}

export const EditQuestion = ({
  question,
  anyApiIsLoading,
  isAssessmentPublished,
  register,
  index,
  removeQuestion,
  control,
  questionIndex,
  getValues,
  formState,
  watch
}: EditQuestionProps) => {
  const { t } = useTranslation();
  const [helpTextPreview, setHelpTextPreview] = useState(false);
  const { errors } = formState;
  const {
    fields: answerOptionFields,
    append: appendAnswerOption,
    remove: removeAnswerOption
  } = useFieldArray({
    name: `questions.items.${index}.answerOptions.items`,
    control
  });
  // Dev Note: This is needed to update the answeroptions field when changing the input type
  const watchers = watch([`questions.items.${index}.inputType`]);
  return (
    <section className="section" key={question.id}>
      <Grid xs={8}>
        <li>
          <label htmlFor={`question-${question.id}-sortingIndex`}>
            {t("Sorting Index")}
          </label>
          <input
            type="number"
            required
            placeholder="0"
            style={{ width: "100%" }}
            id={`question-${question.id}-sortingIndex`}
            disabled={anyApiIsLoading || isAssessmentPublished}
            {...register(`questions.items.${index}.sortingIndex` as const, {
              required: {
                value: true,
                message: QuestionErrors.NoSortingIndex
              }
            })}
          />
          {errors.questions?.items?.[index]?.sortingIndex?.message && (
            <Alert severity="error">
              {t(errors.questions?.items?.[index]?.sortingIndex?.message || "")}
            </Alert>
          )}

          <label htmlFor={`question-${question.id}-title`}>
            {t("Question Title")}
          </label>
          <input
            type="text"
            required
            placeholder="Question Title"
            style={{ width: "100%" }}
            id={`question-${question.id}-title`}
            disabled={anyApiIsLoading || isAssessmentPublished}
            {...register(`questions.items.${index}.title` as const, {
              required: {
                value: true,
                message: QuestionErrors.NoTitle
              }
            })}
          />
          {errors.questions?.items?.[index]?.title?.message && (
            <Alert severity="error">
              {t(errors.questions?.items?.[index]?.title?.message || "")}
            </Alert>
          )}

          <label htmlFor={`question-${question.id}-defaultPrompt`}>
            {t("Default Prompt")}
          </label>
          <input
            type="text"
            placeholder="Default Prompt"
            style={{ width: "100%" }}
            id={`question-${question.defaultPrompt}-defaultPrompt`}
            disabled={anyApiIsLoading || isAssessmentPublished}
            {...register(`questions.items.${index}.defaultPrompt` as const)}
          />

          <label htmlFor={`question-${question.id}-selfAssessmentPrompt`}>
            {t("Self Assessment Prompt")}
          </label>
          <input
            type="text"
            placeholder="Self Assessment Prompt"
            style={{ width: "100%" }}
            id={`question-${question.id}-selfAssessmentPrompt`}
            disabled={anyApiIsLoading || isAssessmentPublished}
            {...register(
              `questions.items.${index}.selfAssessmentPrompt` as const
            )}
          />

          <label htmlFor={`question-${question.id}-helpText`}>
            {t("Help Text")}
          </label>
          <div>
            <input
              id={`question-${question.id}-helpTextPreview`}
              type="checkbox"
              checked={helpTextPreview}
              onChange={() => setHelpTextPreview(!helpTextPreview)}
            />
            <label htmlFor={`question-${question.id}-helpTextPreview`}>
              {`Mode: ${helpTextPreview ? "Preview" : "Input"}`}
            </label>
          </div>
          {helpTextPreview ? (
            <div
              dangerouslySetInnerHTML={{
                __html:
                  getValues(`questions.items.${questionIndex}.helpText`) || ""
              }}
            />
          ) : (
            <textarea
              placeholder="Help Text"
              style={{ width: "100%" }}
              id={`question-${question.id}-helpText`}
              disabled={anyApiIsLoading || isAssessmentPublished}
              {...register(`questions.items.${index}.helpText` as const)}
            />
          )}

          <label htmlFor={`question-${question.id}-inputType`}>
            {t("Input Type")}
          </label>
          <select
            style={{ width: "100%" }}
            id={`question-${question.id}-inputType`}
            disabled={anyApiIsLoading || isAssessmentPublished}
            {...register(`questions.items.${index}.inputType`)}
          >
            <option value={"Radio"}>{t("Radio")}</option>
            <option value={"Feedback"}>{t("Feedback")}</option>
          </select>
          {getValues(`questions.items.${index}.inputType`) ===
            InputType.Radio && (
            <>
              {t("Answer Options:")}
              <ul>
                {answerOptionFields.map((answerOption, index) => (
                  <EditAnswerOption
                    answerOption={answerOption}
                    questionIndex={questionIndex}
                    register={register}
                    answerOptionIndex={index}
                    getValues={getValues}
                    removeAnswerOption={removeAnswerOption}
                    formState={formState}
                    anyApiIsLoading={anyApiIsLoading}
                    isAssessmentPublished={isAssessmentPublished}
                  />
                ))}
                <Grid xs={8}>
                  <button
                    type="button"
                    disabled={anyApiIsLoading || isAssessmentPublished}
                    onClick={() => {
                      const appendedAnswerOption: AnswerOption = {
                        // Dev Note: The unnecessary data will be stripped at time of submission.
                        __typename: "AnswerOption",
                        id: uuid(),
                        anchor: "",
                        sortingIndex: null,
                        helpText: "",
                        value: "",
                        questionId: question.id,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toString()
                      };
                      appendAnswerOption(appendedAnswerOption);
                    }}
                  >
                    {t("Add Answer Option")}
                  </button>
                </Grid>
              </ul>
            </>
          )}
          <button
            type="button"
            disabled={anyApiIsLoading || isAssessmentPublished}
            onClick={() => removeQuestion(index)}
          >
            {t("Delete Question")}
          </button>
        </li>
      </Grid>
    </section>
  );
};
